import { useState } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import { ArrowUpRight, Mail, Phone, Instagram, MapPin } from "lucide-react";
import RevealText from "../components/RevealText";
import MagneticButton from "../components/MagneticButton";

const SUBJECTS = [
  "Renting the Studio",
  "A Photoshoot",
  "Social Media Content",
  "Ad Campaign",
  "Just Saying Hi",
];
const BUDGETS = ["<50k", "50k – 1L", "1L +"];

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

export default function Contact() {
  const [data, setData] = useState({ name: "", email: "", subject: SUBJECTS[0], budget: BUDGETS[1], message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_PUBLIC_KEY) {
      setError("Contact form is not configured yet. Please email us directly at infosaucestudios@gmail.com.");
      return;
    }

    setLoading(true);
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: data.name,
          reply_to: data.email,
          subject: data.subject,
          budget: data.budget,
          message: data.message,
        },
        EMAILJS_PUBLIC_KEY,
      );
      setSubmitted(true);
    } catch {
      setError("Something went wrong sending your message. Please try again or email infosaucestudios@gmail.com.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact" className="pt-[calc(var(--site-header-offset)+2rem)] md:pt-40">
      <section className="px-6 md:px-12 pb-12 md:pb-20">
        <p className="font-body uppercase text-xs tracking-[0.2em] text-black/50 mb-4">Contact — Begin here</p>
        <h1 className="font-display uppercase text-[18vw] md:text-[14vw] leading-[0.84] tracking-mega">
          <RevealText>Let&apos;s</RevealText><br/>
          <RevealText delay={0.06}><em className="font-editorial italic">talk</em><span className="text-[#F42B29]">.</span></RevealText>
        </h1>
      </section>

      <section className="px-6 md:px-12 pb-24 grid grid-cols-12 gap-6 md:gap-12">
        <div className="col-span-12 md:col-span-7">
          {!submitted ? (
            <form onSubmit={onSubmit} className="space-y-8" data-testid="contact-form">
              <div>
                <label className="font-body uppercase text-xs tracking-[0.2em] text-black/50 block mb-2">01 — Your name</label>
                <input required value={data.name} onChange={set("name")} placeholder="What should we call you?"
                  className="w-full bg-transparent border-b border-black/20 focus:border-[#F42B29] outline-none py-3 font-display text-2xl md:text-4xl"
                  data-testid="contact-input-name"
                />
              </div>
              <div>
                <label className="font-body uppercase text-xs tracking-[0.2em] text-black/50 block mb-2">02 — Email</label>
                <input required type="email" value={data.email} onChange={set("email")} placeholder="you@brand.com"
                  className="w-full bg-transparent border-b border-black/20 focus:border-[#F42B29] outline-none py-3 font-display text-2xl md:text-4xl"
                  data-testid="contact-input-email"
                />
              </div>
              <div>
                <label className="font-body uppercase text-xs tracking-[0.2em] text-black/50 block mb-3">03 — I&apos;m interested in</label>
                <div className="flex flex-wrap gap-2">
                  {SUBJECTS.map((s) => (
                    <button type="button" key={s} onClick={() => setData((d) => ({ ...d, subject: s }))}
                      className={`px-4 py-2 border font-body uppercase text-xs tracking-[0.2em] transition-colors ${data.subject === s ? "bg-black text-white border-black" : "border-black/20 hover:border-black"}`}
                      data-testid={`contact-subject-${s.replace(/\s+/g, "-").toLowerCase()}`}
                    >{s}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="font-body uppercase text-xs tracking-[0.2em] text-black/50 block mb-3">04 — Budget</label>
                <div className="flex flex-wrap gap-2">
                  {BUDGETS.map((b) => (
                    <button type="button" key={b} onClick={() => setData((d) => ({ ...d, budget: b }))}
                      className={`px-4 py-2 border font-body uppercase text-xs tracking-[0.2em] transition-colors ${data.budget === b ? "bg-[#F42B29] text-white border-[#F42B29]" : "border-black/20 hover:border-black"}`}
                      data-testid={`contact-budget-${b.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                    >{b}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="font-body uppercase text-xs tracking-[0.2em] text-black/50 block mb-2">05 — Tell us your idea</label>
                <textarea required rows={4} value={data.message} onChange={set("message")} placeholder="A paragraph, a sketch, a half-baked idea — anything."
                  className="w-full bg-transparent border-b border-black/20 focus:border-[#F42B29] outline-none py-3 font-body text-lg md:text-xl resize-none"
                  data-testid="contact-input-message"
                />
              </div>
              {error && (
                <p className="font-body text-sm text-[#F42B29]" data-testid="contact-error" role="alert">
                  {error}
                </p>
              )}
              <MagneticButton
                as="button"
                type="submit"
                disabled={loading}
                className="group inline-flex items-center gap-4 bg-black text-white px-8 py-5 hover:bg-[#F42B29] transition-colors disabled:opacity-60 disabled:pointer-events-none"
                data-testid="contact-submit-btn"
              >
                <span className="font-body uppercase text-sm tracking-[0.2em]">
                  {loading ? "Sending…" : "Send it our way"}
                </span>
                <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform duration-500" />
              </MagneticButton>
            </form>
          ) : (
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} data-testid="contact-success">
              <p className="font-body uppercase text-xs tracking-[0.2em] text-black/50 mb-4">Sent</p>
              <h2 className="font-display text-5xl md:text-7xl uppercase tracking-mega leading-[0.9]">Thanks, {data.name || "friend"}.</h2>
              <p className="font-editorial italic text-2xl md:text-3xl mt-6 max-w-xl">We&apos;ll get back to you within 48 hours — usually faster, sometimes with a question.</p>
              <p className="mt-6 font-script text-3xl text-[#F42B29]">— Team Sauce</p>
            </motion.div>
          )}
        </div>

        <aside className="col-span-12 md:col-span-4 md:col-start-9 mt-10 md:mt-2 space-y-10" data-testid="contact-info">
          <div>
            <p className="font-body uppercase text-xs tracking-[0.2em] text-black/50 mb-3">Studio</p>
            <p className="font-display text-2xl md:text-3xl uppercase tracking-tight">Jodhpur,<br/>Rajasthan</p>
            <p className="font-body text-base text-black/70 mt-3">
              Drop by the studio.<br />
              Just let us know first—we&apos;ll have the chai ready.
            </p>
          </div>
          <div className="flex flex-col gap-4">
            <a href="mailto:infosaucestudios@gmail.com" className="flex items-start gap-3 link-line min-w-0 max-w-full"><Mail className="w-4 h-4 shrink-0 mt-0.5" /><span className="font-body text-base break-all">infosaucestudios@gmail.com</span></a>
            <a href="tel:+919983341510" className="flex items-center gap-3 link-line w-fit"><Phone className="w-4 h-4 shrink-0" /><span className="font-body text-base">+91 99833 41510</span></a>
            <a href="https://instagram.com" className="flex items-center gap-3 link-line w-fit"><Instagram className="w-4 h-4 shrink-0" /><span className="font-body text-base">@saucestudios</span></a>
          </div>
          <div className="aspect-square w-full overflow-hidden border border-black/10">
            <iframe
              title="Sauce Studios on Map"
              src="https://maps.google.com/maps?q=26.2841779,73.0572785&hl=en&z=17&output=embed"
              className="w-full h-full grayscale contrast-110"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="contact-map"
            />
          </div>
          <a
            href="https://www.google.com/maps/place/Sauce+Studios/@26.2841779,73.0572785,17z/data=!3m1!4b1!4m6!3m5!1s0x39418d055c7171dd:0x378c7a5cd71d0e8e!8m2!3d26.2841779!4d73.0572785!16s%2Fg%2F11y8v7j97f?entry=ttu"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 font-body uppercase text-xs tracking-[0.2em] text-black/50 link-line w-fit"
          >
            <MapPin className="w-3.5 h-3.5" /> 26.28° N, 73.06° E
          </a>
        </aside>
      </section>
    </div>
  );
}
